import React, { useState, useEffect, useRef, memo } from 'react';
import { usePhotoCache } from '../contexts/PhotoCacheContext';

/**
 * Shared ListHeroPhoto component for list preview cards.
 * Uses global photo caching and lazy loading.
 */
const ListHeroPhoto = memo(function ListHeroPhoto({
  placeId,
  altText,
  storedPhotoUrl = null,
  photoUrlSavedAt = null,
  customPhoto = null,
}) {
  const { getPhotoUrl, refreshPhotoUrl } = usePhotoCache();
  const [photoUrl, setPhotoUrl] = useState(null);
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef(null);
  const fetchAttemptedRef = useRef(false);
  const refreshAttemptedRef = useRef(false);

  // Intersection Observer for lazy loading
  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.unobserve(element);
          }
        });
      },
      {
        rootMargin: '200px', // Start loading 200px before visible for hero images
        threshold: 0,
      }
    );

    observer.observe(element);

    return () => {
      observer.unobserve(element);
    };
  }, []);

  // Reset state when placeId changes
  useEffect(() => {
    setPhotoUrl(null);
    setHasError(false);
    setIsLoading(true);
    fetchAttemptedRef.current = false;
    refreshAttemptedRef.current = false;
  }, [placeId]);

  // Fetch photo when visible
  useEffect(() => {
    if (!isVisible || fetchAttemptedRef.current) return;

    const fetchPhoto = async () => {
      fetchAttemptedRef.current = true;

      // If we have a stored photo URL, use it directly without going through cache
      // This avoids unnecessary async operations and API calls
      if (storedPhotoUrl) {
        setPhotoUrl(storedPhotoUrl);
        setIsLoading(false);
        return;
      }

      if (!placeId) {
        setHasError(true);
        setIsLoading(false);
        return;
      }

      try {
        // Pass stored photo data to avoid redundant API calls
        const url = await getPhotoUrl(placeId, {
          storedPhotoUrl,
          photoUrlSavedAt,
        });
        setPhotoUrl(url);
        setHasError(false);
      } catch (err) {
        setHasError(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPhoto();
  }, [isVisible, placeId, getPhotoUrl, storedPhotoUrl, photoUrlSavedAt]);

  // If customPhoto is provided, use it directly (after all hooks)
  if (customPhoto) {
    return (
      <img
        src={customPhoto}
        alt={altText}
        className="list-hero-photo"
      />
    );
  }

  // Placeholder SVG for lists
  const placeholderSvg = (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
      <circle cx="12" cy="10" r="3"/>
    </svg>
  );

  // Show placeholder if error or no photo available
  if (hasError || (!placeId && !isLoading)) {
    return (
      <div ref={containerRef} className="list-hero-placeholder">
        {placeholderSvg}
      </div>
    );
  }

  // Show loading placeholder while fetching
  if (!isVisible || isLoading || !photoUrl) {
    return (
      <div ref={containerRef} className="list-hero-placeholder loading">
        {placeholderSvg}
      </div>
    );
  }

  // Handle image load error - try refreshing once before giving up
  const handleImageError = async () => {
    // If we haven't tried refreshing yet, attempt to get a fresh URL
    if (!refreshAttemptedRef.current && placeId) {
      refreshAttemptedRef.current = true;
      setIsLoading(true);
      try {
        const freshUrl = await refreshPhotoUrl(placeId);
        setPhotoUrl(freshUrl);
        setIsLoading(false);
      } catch (err) {
        // Refresh failed, show error state
        setHasError(true);
        setIsLoading(false);
      }
    } else {
      // Already tried refreshing, give up
      setHasError(true);
    }
  };

  return (
    <img
      ref={containerRef}
      src={photoUrl}
      alt={altText}
      className="list-hero-photo"
      onError={handleImageError}
    />
  );
});

export default ListHeroPhoto;
